import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { Model } from '../../../types/models/model'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import {
  selectSelectedModelId,
  setSelectedModelId,
  selectCompareModelId,
  setCompareModelId,
} from '../../../store/slices/modelSlice'

import './styles.scss'
import {
  GitBranch,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Package,
  Brain,
  Cpu,
  BarChart3,
  TrendingUp,
} from 'lucide-react'
import ModelTreeVisualization from '../../tree/ModelTreeVisualization'
import { getLatestMetrics, getPerformanceComparison } from '../../tree/ModelTreeNode'
import { ProjectAnnotationType } from '../../../types/models/project'
import Header from './Header'
import ResultsInfoComponent from './ResultsInfo'
import ActionBarComponent from './ActionBar'
import FilterBar from './FilterBar'
import Sidebar from './Sidebar/Sidebar'
import { useModelCatalogData } from './hooks/useModelCatalogData'
import { useModelFilters, useApplyFilters, useSort } from './hooks/useModelFilters'
import ModelsGrid from './ModelsGrid/ModelsGrid'
import ModelCard from './ModelsGrid/ModelCard'

// styled modal removed; use Bootstrap utilities on Modal/Header/Body

// Catalog header moved to Header.tsx

// Filter bar moved to FilterBar.tsx

// ModelsGrid moved to ModelsGrid/ModelsGrid.tsx

// ResultsInfo moved to ResultsInfo.tsx

// Action bar elements moved to ActionBar.tsx

// Add new type for sort options
type SortOption = {
  label: string
  key: string
  getValue: (model: any) => number
  annotationType?: ProjectAnnotationType
}

const getSortOptions = (projectAnnotationType?: string): SortOption[] => {
  const baseOptions: SortOption[] = [
    {
      label: 'Latest',
      key: 'date',
      getValue: (model) => new Date(model.created_at).getTime(),
    },
  ]

  if (projectAnnotationType === 'bounding_box') {
    baseOptions.push(
      {
        label: 'Best mAP@50',
        key: 'mAP50',
        getValue: (model) => {
          return model.metrics?.['validation:mAP50'] || 0
        },
        annotationType:
          projectAnnotationType === 'bounding_box' ? 'bounding_box' : 'oriented_bounding_box',
      },
      {
        label: 'Best Precision',
        key: 'precision',
        getValue: (model) => {
          return model.metrics?.['validation:precision'] || 0
        },
        annotationType:
          projectAnnotationType === 'bounding_box' ? 'bounding_box' : 'oriented_bounding_box',
      },
      {
        label: 'Best Recall',
        key: 'recall',
        getValue: (model) => {
          return model.metrics?.['validation:recall'] || 0
        },
        annotationType:
          projectAnnotationType === 'bounding_box' ? 'bounding_box' : 'oriented_bounding_box',
      },
      {
        label: 'Best F1 Score',
        key: 'f1',
        getValue: (model) => {
          const precision = model.metrics?.['validation:precision']
          const recall = model.metrics?.['validation:recall']
          if (precision && recall) {
            return (2 * (precision * recall)) / (precision + recall)
          }
          return 0
        },
        annotationType:
          projectAnnotationType === 'bounding_box' ? 'bounding_box' : 'oriented_bounding_box',
      }
    )
  } else if (projectAnnotationType === 'classification') {
    baseOptions.push(
      {
        label: 'Best Val Accuracy',
        key: 'val_accuracy',
        getValue: (model) => {
          return model.metrics?.['validation:accuracy'] || 0
        },
        annotationType: 'classification',
      },
      {
        label: 'Best Train Accuracy',
        key: 'train_accuracy',
        getValue: (model) => {
          return model.metrics?.['train:accuracy'] || 0
        },
        annotationType: 'classification',
      }
    )
  }

  // Also check for models with classification type in hyperparameters
  if (!projectAnnotationType) {
    baseOptions.push(
      {
        label: 'Best Val Accuracy',
        key: 'val_accuracy',
        getValue: (model) => {
          return model.metrics?.['validation:accuracy'] || 0
        },
        annotationType: 'classification',
      },
      {
        label: 'Best Train Accuracy',
        key: 'train_accuracy',
        getValue: (model) => {
          return model.metrics?.['train:accuracy'] || 0
        },
        annotationType: 'classification',
      }
    )
  }

  return baseOptions
}

// Helper to flatten the model tree into a flat array of models
function flattenModelTree(nodes: any[] = []): any[] {
  let result: any[] = []
  for (const node of nodes) {
    result.push(node)
    if (node.children && node.children.length > 0) {
      result = result.concat(flattenModelTree(node.children))
    }
  }
  return result
}

interface SelectModelTreeModalProps {
  show: boolean
  onHide: () => void
  onModelSelect: (model: Model) => void
  selectedModelId: string | null
  selectionMode?: 'base' | 'compare'
  excludeModelIds?: string[]
}

const SelectModelTreeModal: React.FC<SelectModelTreeModalProps> = ({
  show,
  onHide,
  onModelSelect,
  selectedModelId,
  selectionMode = 'base',
  excludeModelIds = [],
}) => {
  const dispatch = useAppDispatch()
  const reduxBaseSelectedModelId = useAppSelector(selectSelectedModelId)
  const reduxCompareSelectedModelId = useAppSelector(selectCompareModelId)
  const reduxSelectedModelId =
    selectionMode === 'compare' ? reduxCompareSelectedModelId : reduxBaseSelectedModelId

  const { project, isLoading, flatNodes, organize } = useModelCatalogData()

  const { search, setSearch, filterType, setFilterType, sortBy, setSortBy, viewMode, setViewMode } =
    useModelFilters('grid')

  const [selectedParent, setSelectedParent] = useState<any>(null)
  const [selectedModelForDetail, setSelectedModelForDetail] = useState<any>(null)
  const [showDetailSidebar, setShowDetailSidebar] = useState(false)

  const filteredNodes = useApplyFilters(flatNodes, search, filterType)
  const filteredNodesExcluding = excludeModelIds.length
    ? filteredNodes.filter((node: any) => !excludeModelIds.includes(node.model.id))
    : filteredNodes
  const sortedNodes = useSort(filteredNodesExcluding, sortBy, () =>
    getSortOptions(project?.annotation_type)
  )

  const handleParentSelect = (node: any) => {
    if (node.children && node.children.length > 0) {
      setSelectedParent(node)
    }
  }

  const handleBack = () => {
    setSelectedParent(null)
  }

  const handleIconClick = (e: React.MouseEvent, node: any) => {
    e.stopPropagation()
    handleParentSelect(node)
  }

  const handleModelDetailClick = (e: React.MouseEvent, node: any) => {
    e.stopPropagation()
    setSelectedModelForDetail(node)
    setShowDetailSidebar(true)
  }

  const handleDetailSidebarClose = () => {
    setShowDetailSidebar(false)
    setSelectedModelForDetail(null)
  }

  // Reset to all models view when modal is opened
  useEffect(() => {
    if (show) {
      setSelectedParent(null)
      setFilterType('all')
      setSearch('')
      setSelectedModelForDetail(null)
      setShowDetailSidebar(false)
    }
  }, [setFilterType, setSearch, show])

  const handleModelSelection = (model: any) => {
    if (selectionMode === 'compare') {
      dispatch(setCompareModelId(model.id))
    } else {
      dispatch(
        setSelectedModelId({
          modelId: model.id,
          projectId: project?.id || '',
        })
      )
    }
  }

  const handleAcceptSelection = () => {
    if (reduxSelectedModelId) {
      const selectedNode = flatNodes.find((node) => node.model.id === reduxSelectedModelId)
      if (selectedNode) {
        onModelSelect(selectedNode.model)
        onHide()
      }
    }
  }

  const handleClearSelection = () => {
    if (selectionMode === 'compare') {
      dispatch(setCompareModelId(undefined))
    } else {
      dispatch(
        setSelectedModelId({
          modelId: undefined,
          projectId: project?.id || '',
        })
      )
    }
  }

  // no-op

  const organizeModels = organize

  const renderModelCard = (node: any, type: 'parent' | 'branch' | 'leaf') => {
    const parentModel = flatNodes.find((n) => n.model.id === node.parent_id)?.model
    const hasChildren = node.children && node.children.length > 0

    return (
      <ModelCard
        key={node.model.id}
        node={node}
        type={type}
        isSelected={reduxSelectedModelId === node.model.id}
        isActive={selectedModelId === node.model.id}
        onClick={() => handleModelSelection(node.model)}
        onDetailClick={(e) => handleModelDetailClick(e, node)}
        onBranchClick={hasChildren ? (e) => handleIconClick(e, node) : undefined}
        project={project}
        viewMode={viewMode}
        comparison={getPerformanceComparison(node.model, parentModel, project?.annotation_type)}
        metricsDisplay={(function () {
          const model = node.model
          const isClassification =
            project?.annotation_type === 'classification' ||
            model.parameters?.hyperparameters?.type === 'classification'
          if (isClassification) {
            const trainingStats = model.training_stats?.classification?.training_job_metrics
            let bestEpoch = 'N/A'
            if (trainingStats) {
              const jobKeys = Object.keys(trainingStats)
              if (jobKeys.length > 0) {
                const entries = trainingStats[jobKeys[0]]?.training_job_entries || []
                let bestAcc = 0
                entries.forEach((entry: any) => {
                  if (entry.accuracy > bestAcc) {
                    bestAcc = entry.accuracy
                    bestEpoch = entry.epoch
                  }
                })
              }
            }
            return [
              {
                label: 'Train Acc',
                value: model.metrics?.['train:accuracy']
                  ? `${((model.metrics['train:accuracy'] as number) * 100).toFixed(1)}%`
                  : 'N/A',
                icon: TrendingUp,
              },
              {
                label: 'Val Acc',
                value: model.metrics?.['validation:accuracy']
                  ? `${((model.metrics['validation:accuracy'] as number) * 100).toFixed(1)}%`
                  : 'N/A',
                icon: BarChart3,
              },
              { label: 'Best Epoch', value: bestEpoch.toString(), icon: ArrowUp },
              {
                label: 'Total Epochs',
                value:
                  model.parameters?.hyperparameters?.hyperparameters?.training?.epochs?.value ||
                  'N/A',
                icon: ArrowUpDown,
              },
            ]
          }
          const metrics = getLatestMetrics(model)
          if (!metrics) return undefined
          if (project?.annotation_type === 'bounding_box') {
            const precision = model.metrics?.['validation:precision']
            const recall = model.metrics?.['validation:recall']
            let f1Value = 'N/A'
            if (precision && recall) {
              const f1 =
                (2 * (precision as number) * (recall as number)) /
                ((precision as number) + (recall as number))
              f1Value = `${(f1 * 100).toFixed(1)}%`
            }
            return [
              {
                label: 'mAP@50',
                value: model.metrics?.['validation:mAP50']
                  ? `${((model.metrics['validation:mAP50'] as number) * 100).toFixed(1)}%`
                  : 'N/A',
                icon: BarChart3,
              },
              {
                label: 'Precision',
                value: precision ? `${((precision as number) * 100).toFixed(1)}%` : 'N/A',
                icon: ArrowUp,
              },
              {
                label: 'Recall',
                value: recall ? `${((recall as number) * 100).toFixed(1)}%` : 'N/A',
                icon: ArrowDown,
              },
              { label: 'F1 Score', value: f1Value, icon: TrendingUp },
            ]
          }
          return [
            {
              label: 'Status',
              value: (function () {
                const s = node.model.state
                if (s === 'TRAINING') return 'training'
                if (s === 'TRAINED') return 'trained'
                if (s === 'ERR') return 'error'
                return 'unknown'
              })(),
              icon: Cpu,
            },
            {
              label: 'Epochs',
              value:
                node.model.parameters?.hyperparameters?.hyperparameters?.training?.epochs?.value ||
                'N/A',
              icon: ArrowUpDown,
            },
            { label: 'Dataset', value: node.model.datasetid || 'N/A', icon: Package },
            {
              label: 'Created',
              value: new Date(node.model.created_at).toLocaleDateString(),
              icon: Brain,
            },
          ]
        })()}
      />
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center py-5 px-3 text-secondary"
          style={{ minHeight: 300 }}
        >
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: 60, height: 60, borderWidth: '3px' }}
            role="status"
          />
          <h4 className="fw-semibold text-white mb-2">Loading Model Catalog</h4>
          <p className="mb-0">Fetching all available models and their relationships...</p>
        </div>
      )
    }

    if (selectedParent) {
      return (
        <div style={{ position: 'relative', height: '100%' }}>
          <div
            className="border-bottom border-secondary"
            style={{ padding: '1rem 2rem', background: '#2a2a2a' }}
          >
            <button
              onClick={handleBack}
              className="btn text-white fw-semibold d-flex align-items-center gap-2 shadow"
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: 12,
                padding: '.75rem 1.5rem',
              }}
            >
              <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
              Back to Model Catalog
            </button>
          </div>
          <ModelTreeVisualization
            parent={selectedParent}
            onBack={handleBack}
            onModelSelect={handleModelSelection}
            project={project}
          />
        </div>
      )
    }

    if (!sortedNodes.length) {
      return (
        <div className="text-center py-5 px-3 text-secondary">
          <div className="mx-auto mb-3" style={{ width: 80, height: 80, opacity: 0.3 }}>
            <div className="w-100 h-100 d-flex align-items-center justify-content-center">
              <Brain className="w-100 h-100" />
            </div>
          </div>
          <h3 className="h4 fw-bold text-white mb-2">No Models Available</h3>
          <p className="fs-5 mx-auto" style={{ maxWidth: 500, lineHeight: 1.5 }}>
            {search.trim() || filterType !== 'all'
              ? 'No models match your current search and filter criteria. Try adjusting your filters or search terms.'
              : 'There are currently no models in this project. Models will appear here once they have been created and trained.'}
          </p>
        </div>
      )
    }

    const { parentModels, branchModels, leafModels } = organizeModels(sortedNodes)

    const ModelTypeIcon = ({ type, children }: { type: string; children: React.ReactNode }) => {
      const background =
        type === 'parent'
          ? 'linear-gradient(135deg,#667eea,#764ba2)'
          : type === 'branch'
          ? 'linear-gradient(135deg,#7c3aed,#a855f7)'
          : type === 'leaf'
          ? 'linear-gradient(135deg,#059669,#10b981)'
          : 'linear-gradient(135deg,#6b7280,#9ca3af)'
      return (
        <div
          className="d-flex align-items-center justify-content-center text-white"
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background,
            boxShadow: '0 2px 4px rgba(0,0,0,.3)',
          }}
        >
          {children}
        </div>
      )
    }

    const GroupHeader = ({
      title,
      count,
      description,
    }: {
      title: React.ReactNode
      count: number
      description: string
    }) => (
      <div
        className="d-flex align-items-center gap-2 mb-3 p-3 rounded-4 border"
        style={{
          background: 'linear-gradient(135deg, rgba(102,126,234,.125), rgba(118,74,162,.125))',
          borderColor: '#667eea40',
        }}
      >
        <h3 className="m-0 h5 fw-semibold text-white d-flex align-items-center gap-2">{title}</h3>
        <span className="badge bg-gradient-primary ms-1">{count}</span>
        <span className="text-secondary fst-italic ms-auto">{description}</span>
      </div>
    )

    return (
      <>
        <ResultsInfoComponent
          totalShown={sortedNodes.length}
          totalAll={flatNodes.length}
          viewMode={viewMode}
        />

        {parentModels.length > 0 && (
          <div className="mb-4">
            <GroupHeader
              title={
                <>
                  <ModelTypeIcon type="parent">
                    <Cpu size={20} />
                  </ModelTypeIcon>
                  Foundation Models
                </>
              }
              count={parentModels.length}
              description="Base models without dependencies"
            />
            <ModelsGrid viewMode={viewMode}>
              {parentModels.map((node) => renderModelCard(node, 'parent'))}
            </ModelsGrid>
          </div>
        )}

        {branchModels.length > 0 && (
          <div className="mb-4">
            <GroupHeader
              title={
                <>
                  <ModelTypeIcon type="branch">
                    <GitBranch size={20} />
                  </ModelTypeIcon>
                  Branch Models
                </>
              }
              count={branchModels.length}
              description="Models with both parents and children"
            />
            <ModelsGrid viewMode={viewMode}>
              {branchModels.map((node) => renderModelCard(node, 'branch'))}
            </ModelsGrid>
          </div>
        )}

        {leafModels.length > 0 && (
          <div className="mb-4">
            <GroupHeader
              title={
                <>
                  <ModelTypeIcon type="leaf">
                    <BarChart3 size={20} />
                  </ModelTypeIcon>
                  Specialized Models
                </>
              }
              count={leafModels.length}
              description="Final models derived from other models"
            />
            <ModelsGrid viewMode={viewMode}>
              {leafModels.map((node) => renderModelCard(node, 'leaf'))}
            </ModelsGrid>
          </div>
        )}
      </>
    )
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered data-bs-theme="dark">
      <Modal.Header
        closeButton
        closeVariant="white"
        className="bg-gradient-primary text-white border-0 px-4 py-3"
      >
        <Modal.Title className="d-flex align-items-center gap-2">
          <Brain size={24} />
          Model Catalog
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="bg-dark p-0 d-flex flex-column"
        style={{ maxHeight: '80vh', overflow: 'hidden' }}
      >
        <Header
          selectedParent={selectedParent}
          selectedModelId={selectedModelId}
          project={project as any}
          flatNodes={flatNodes}
        />

        {!selectedParent && (
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            filterType={filterType}
            onFilterTypeChange={setFilterType}
            sortBy={sortBy}
            onSortChange={setSortBy}
            sortOptions={getSortOptions(project?.annotation_type).map((o) => ({
              key: o.key,
              label: o.label,
            }))}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        )}

        <div className="d-flex flex-grow-1 overflow-hidden">
          <div
            className="flex-grow-1 overflow-auto p-4 bg-dark"
            style={{
              transition: 'all .3s ease',
              width: showDetailSidebar ? '45%' : '100%',
              opacity: showDetailSidebar ? 0.3 : 1,
              pointerEvents: showDetailSidebar ? 'none' : 'auto',
              filter: showDetailSidebar ? 'blur(2px)' : 'none',
            }}
          >
            {renderContent()}
          </div>
          {selectedModelForDetail && project && (
            <Sidebar
              model={selectedModelForDetail.model}
              project={project}
              onClose={handleDetailSidebarClose}
            />
          )}
        </div>

        <ActionBarComponent
          selectedModelName={
            flatNodes.find((node) => node.model.id === reduxSelectedModelId)?.model.name || 'Model'
          }
          hasSelection={!!reduxSelectedModelId}
          onClearSelection={handleClearSelection}
          onCancel={onHide}
          onConfirm={handleAcceptSelection}
          confirmDisabled={!reduxSelectedModelId}
        />
      </Modal.Body>
    </Modal>
  )
}

export default SelectModelTreeModal
